import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

export type SerialFormVariant = 'receive' | 'pick' | 'adjustUp' | 'adjustDown';

export interface SerialStockRow {
  serialNumber: string;
  receivedAt?: string | null;
}

export interface SerialRefusal {
  serialNumber: string;
  reason?: string;
}

interface SerialRow {
  serial: string;
  receivedAt: string | null;
  selected: boolean;
  off: boolean;
  bad: boolean;
  reasonKey: string | null;
}

interface NoteMessage {
  key: string;
  params?: { [k: string]: any };
  kind: 'info' | 'ok' | 'bad';
}

interface SkippedSerial {
  serial: string;
  reasonKey: string;
}

// Serial number block that replaces the quantity input for serialized products.
// Receive and adjust-up collect typed/scanned/pasted serials; pick and adjust-down
// tick serials out of the in-stock list of one storage. The quantity is always the
// count, never typed. The parent owns the request and feeds 422 refusals back in.
@Component({
  selector: 'app-serial-form',
  templateUrl: './serial-form.component.html',
  styleUrls: ['./serial-form.component.scss']
})
export class SerialFormComponent implements OnChanges {
  @Input() variant: SerialFormVariant = 'receive';
  // Adjust variants: exactly this many serials are needed.
  @Input() needed: number | null = null;
  // Serial prefixes of the product (case-insensitive); empty = no client check.
  @Input() patterns: string[] = [];
  // Pick variants: the in-stock serials of the storage.
  @Input() stock: SerialStockRow[] | null = null;
  @Input() stockState: 'loading' | 'ready' | 'error' = 'ready';
  @Input() refused: SerialRefusal[] = [];
  @Input() failed = false;
  // The server saw a different on hand than the form was opened with.
  @Input() stale = false;
  @Input() staleOnHand: number | null = null;
  @Input() disabled = false;

  @Output() serialsChange = new EventEmitter<string[]>();
  @Output() blockedChange = new EventEmitter<boolean>();
  @Output() reload = new EventEmitter<void>();
  @Output() retry = new EventEmitter<void>();

  @ViewChild('entryInput') entryInput: ElementRef<HTMLInputElement>;

  readonly filterFrom = 8;

  list: string[] = [];
  selected = new Set<string>();
  entry = '';
  filter = '';
  note: NoteMessage | null = null;
  pasteOpen = false;
  pasteText = '';
  pasteAdded = 0;
  pasteSkipped: SkippedSerial[] = [];
  pasteDone = false;
  refusedActive = false;
  rows: SerialRow[] = [];
  private refusedMap = new Map<string, string>();
  private highlight = new Set<string>();

  get isPick(): boolean { return this.variant === 'pick' || this.variant === 'adjustDown'; }
  get isAdjust(): boolean { return this.variant === 'adjustUp' || this.variant === 'adjustDown'; }
  get count(): number { return this.isPick ? this.selected.size : this.list.length; }
  get isFull(): boolean { return this.isAdjust && this.needed != null && this.count >= this.needed; }
  get stockTotal(): number { return this.stock ? this.stock.length : 0; }
  get listTotal(): number { return this.isPick ? this.stockTotal : this.list.length; }
  get term(): string { return (this.isPick && this.variant === 'pick' ? this.entry : this.filter).trim().toLowerCase(); }
  get showFilter(): boolean { return this.variant !== 'pick' && this.listTotal >= this.filterFrom; }
  get progress(): number { return this.needed ? Math.min(100, (this.count / this.needed) * 100) : 0; }
  get shownCount(): number { return this.isPick ? this.rows.length : 0; }
  get allShownSelected(): boolean { return this.shownCount > 0 && this.rows.every(r => r.selected); }
  get someShownSelected(): boolean { return this.rows.some(r => r.selected); }
  get refusedCount(): number { return this.refused ? this.refused.length : 0; }
  get actionKey(): string {
    return ({ receive: 'serialForm.actionReceived', pick: 'serialForm.actionPicked', adjustUp: 'serialForm.actionAdded', adjustDown: 'serialForm.actionTakenOut' } as any)[this.variant];
  }
  get untick(): boolean { return this.isPick; }

  ngOnChanges(changes: SimpleChanges): void {
    const resetNeeded = (changes.variant && !changes.variant.firstChange) || (changes.needed && !changes.needed.firstChange);
    if (resetNeeded) {
      this.list = [];
      this.selected.clear();
      this.entry = '';
      this.filter = '';
      this.note = null;
      this.pasteText = '';
      this.pasteSkipped = [];
      this.pasteDone = false;
      this.refusedActive = false;
      this.refusedMap.clear();
      this.emitLater();
    }
    if (changes.stock && this.stock) {
      const live = new Set(this.stock.map(r => r.serialNumber));
      let dropped = false;
      this.selected.forEach(s => { if (!live.has(s)) { this.selected.delete(s); dropped = true; } });
      if (dropped) { this.emitLater(); }
    }
    if (changes.refused) {
      this.refusedMap.clear();
      (this.refused || []).forEach(r => this.refusedMap.set(String(r.serialNumber).toLowerCase(), r.reason || ''));
      this.refusedActive = this.refusedMap.size > 0;
      this.emitLater();
      this.scrollToTop();
    }
    this.rebuild();
  }

  // ---- shared plumbing

  private values(): string[] {
    if (!this.isPick) { return this.list.slice(); }
    return (this.stock || []).map(r => r.serialNumber).filter(s => this.selected.has(s));
  }

  private isBlocked(): boolean {
    if (this.refusedMap.size === 0) { return false; }
    return this.values().some(s => this.refusedMap.has(s.toLowerCase()));
  }

  // Input-driven changes run inside the parent's change detection: tell the parent after it.
  private emitLater(): void {
    Promise.resolve().then(() => this.emit());
  }

  private emit(): void {
    this.serialsChange.emit(this.values());
    this.blockedChange.emit(this.isBlocked());
  }

  private changed(): void {
    this.refusedActive = false;
    this.emit();
    this.rebuild();
  }

  private normalize(value: string): string {
    return (value || '').trim().toUpperCase();
  }

  private matchesPattern(serial: string): boolean {
    if (!this.patterns || this.patterns.length === 0) { return true; }
    const s = serial.toLowerCase();
    return this.patterns.some(p => s.startsWith(String(p).toLowerCase()));
  }

  get patternHint(): string { return this.patterns && this.patterns.length ? this.patterns[0] + '…' : ''; }

  private rebuild(): void {
    const term = this.term;
    const rows: SerialRow[] = [];
    if (this.isPick) {
      for (const r of (this.stock || [])) {
        if (term && !r.serialNumber.toLowerCase().includes(term)) { continue; }
        const selected = this.selected.has(r.serialNumber);
        const key = r.serialNumber.toLowerCase();
        rows.push({
          serial: r.serialNumber,
          receivedAt: r.receivedAt || null,
          selected,
          off: this.variant === 'adjustDown' && this.isFull && !selected,
          bad: selected && this.refusedMap.has(key),
          reasonKey: selected && this.refusedMap.has(key) ? this.reasonKey(this.refusedMap.get(key)) : null
        });
      }
    } else {
      for (let i = this.list.length - 1; i >= 0; i--) {
        const serial = this.list[i];
        if (term && !serial.toLowerCase().includes(term)) { continue; }
        const key = serial.toLowerCase();
        rows.push({
          serial,
          receivedAt: null,
          selected: false,
          off: false,
          bad: this.refusedMap.has(key),
          reasonKey: this.refusedMap.has(key) ? this.reasonKey(this.refusedMap.get(key)) : null
        });
      }
    }
    // Refused rows first, the rest keep their order.
    this.rows = [...rows.filter(r => r.bad), ...rows.filter(r => !r.bad)];
  }

  reasonKey(reason: string): string {
    switch (reason) {
      case 'already_in_stock': return 'serialForm.reasonAlreadyInStock';
      case 'bad_pattern': return 'serialForm.reasonPattern';
      case 'not_in_storage':
      case 'not_in_stock': return 'serialForm.reasonGone';
      case 'duplicate_in_request': return 'serialForm.reasonTwice';
      default: return 'serialForm.reasonUnknown';
    }
  }

  private scrollToTop(): void {
    setTimeout(() => {
      const el = document.querySelector('.sf-scroll');
      if (el) { el.scrollTop = 0; }
    });
  }

  trackRow(_: number, row: SerialRow): string { return row.serial; }

  // ---- enter variants (receive, adjust up)

  add(): void {
    if (this.isFull || this.disabled) { return; }
    const serial = this.normalize(this.entry);
    if (!serial) { return; }
    const problem = this.entryProblem(serial);
    if (problem) {
      this.note = { key: problem, params: { serial, pattern: this.patternHint }, kind: 'bad' };
      return;
    }
    this.list.push(serial);
    this.entry = '';
    this.note = { key: 'serialForm.added', params: { serial }, kind: 'ok' };
    this.changed();
    this.focusEntry();
  }

  private entryProblem(serial: string): string | null {
    if (this.list.some(s => s.toLowerCase() === serial.toLowerCase())) { return 'serialForm.duplicate'; }
    if (!this.matchesPattern(serial)) { return 'serialForm.badPattern'; }
    return null;
  }

  removeSerial(serial: string): void {
    this.list = this.list.filter(s => s !== serial);
    this.refusedMap.delete(serial.toLowerCase());
    this.note = null;
    this.changed();
  }

  clearAll(): void {
    this.list = [];
    this.refusedMap.clear();
    this.note = null;
    this.pasteDone = false;
    this.changed();
  }

  togglePaste(): void {
    this.pasteOpen = !this.pasteOpen;
  }

  get pasteTokens(): string[] {
    return this.pasteText.split(/[\s,;]+/).map(t => t.trim()).filter(t => t.length > 0);
  }

  addPasted(): void {
    const left: string[] = [];
    const skipped: SkippedSerial[] = [];
    const seen = new Set<string>();
    let added = 0;
    for (const token of this.pasteTokens) {
      const serial = this.normalize(token);
      const key = serial.toLowerCase();
      if (seen.has(key)) {
        skipped.push({ serial, reasonKey: 'serialForm.reasonTwiceInPaste' });
        left.push(serial);
        continue;
      }
      seen.add(key);
      if (this.needed != null && this.list.length >= this.needed) {
        skipped.push({ serial, reasonKey: 'serialForm.reasonOver' });
        left.push(serial);
        continue;
      }
      const problem = this.entryProblem(serial);
      if (problem) {
        skipped.push({ serial, reasonKey: problem === 'serialForm.duplicate' ? 'serialForm.reasonInList' : 'serialForm.reasonPattern' });
        left.push(serial);
        continue;
      }
      this.list.push(serial);
      added++;
    }
    this.pasteAdded = added;
    this.pasteSkipped = skipped;
    this.pasteDone = true;
    this.pasteText = left.join('\n');
    if (added > 0) { this.changed(); }
  }

  // ---- pick variants (pick, adjust down)

  toggle(row: SerialRow): void {
    if (this.disabled || (row.off && !row.selected)) { return; }
    if (row.selected) { this.selected.delete(row.serial); } else { this.selected.add(row.serial); }
    this.changed();
  }

  toggleAllShown(): void {
    if (this.disabled) { return; }
    if (this.allShownSelected) { this.rows.forEach(r => this.selected.delete(r.serial)); } else { this.rows.forEach(r => this.selected.add(r.serial)); }
    this.changed();
  }

  onSearchEnter(): void {
    const serial = this.entry.trim();
    if (!serial) { return; }
    const hit = (this.stock || []).find(r => r.serialNumber.toLowerCase() === serial.toLowerCase());
    if (hit) {
      this.selected.add(hit.serialNumber);
      this.entry = '';
      this.note = { key: 'serialForm.ticked', params: { serial: hit.serialNumber }, kind: 'ok' };
      this.changed();
    } else if (this.rows.length === 0) {
      this.note = { key: 'serialForm.notInStorage', params: { serial }, kind: 'bad' };
    }
  }

  onFilterInput(): void {
    if (this.variant === 'pick') { this.note = null; }
    this.rebuild();
  }

  clearFilter(): void {
    this.filter = '';
    this.entry = '';
    this.rebuild();
  }

  // ---- refusals

  dismissRefused(): void {
    if (this.isPick) {
      this.refusedMap.forEach((_, key) => {
        const hit = (this.stock || []).find(r => r.serialNumber.toLowerCase() === key);
        if (hit) { this.selected.delete(hit.serialNumber); }
      });
    } else {
      this.list = this.list.filter(s => !this.refusedMap.has(s.toLowerCase()));
    }
    this.refusedMap.clear();
    this.changed();
  }

  private focusEntry(): void {
    setTimeout(() => this.entryInput?.nativeElement.focus());
  }
}

// Reads a failed save: a 422 with serialErrors marks rows, anything else is a generic failure.
export function readSerialFailure(error: any): { refused: SerialRefusal[]; failed: boolean; mismatch?: boolean; onHand?: number } {
  const body = error && error.error;
  if (error && error.status === 422 && body && body.message === 'serialNumbers.quantityMismatch') {
    return { refused: [], failed: true, mismatch: true, onHand: body.onHand };
  }
  if (error && error.status === 422 && body && body.message === 'serialNumbers.invalid' && Array.isArray(body.serialErrors)) {
    return { refused: body.serialErrors.map((e: any) => ({ serialNumber: e.serialNumber, reason: e.reason })), failed: false };
  }
  return { refused: [], failed: true };
}

// Loads the serial-relevant facts of a product: serialized flag and the serial patterns.
export function readSerialProduct(res: any): { serialized: boolean; patterns: string[] } {
  const product = res && res.product ? res.product : {};
  const patterns = Array.isArray(product.serialPatterns)
    ? product.serialPatterns.map((p: any) => (typeof p === 'string' ? p : p && p.serialPattern)).filter((p: any) => !!p)
    : [];
  return { serialized: Number(product.prdIsSerialized) === 1, patterns };
}
